import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Calendar, Globe } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';

const FormSubmissions = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [form, setForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFormAndSubmissions();
  }, [formId]);

  const fetchFormAndSubmissions = async () => {
    try {
      setLoading(true);

      // Fetch form details
      const formResponse = await axios.get(`/api/forms/${formId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForm(formResponse.data);

      // Fetch submissions
      const submissionsResponse = await axios.get(`/api/forms/${formId}/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(submissionsResponse.data || []);
    } catch (err) {
      setError('Failed to fetch submissions');
      console.error('Error fetching submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (submissions.length === 0) {
      alert('No submissions to export');
      return;
    }

    // Get all unique field names from submissions
    const allFields = new Set();
    submissions.forEach(submission => {
      // submission.data is already an object (JSONB from backend)
      const data = submission.data;
      Object.keys(data).forEach(key => allFields.add(key));
    });

    // Create CSV header
    const headers = ['Submission ID', 'Submitted At', 'IP Address', ...Array.from(allFields)];
    let csv = headers.join(',') + '\n';

    // Add rows
    submissions.forEach(submission => {
      // submission.data is already an object (JSONB from backend)
      const data = submission.data;
      const row = [
        submission.id,
        format(new Date(submission.created_at), 'yyyy-MM-dd HH:mm:ss'),
        submission.ip_address || '',
        ...Array.from(allFields).map(field => {
          const value = data[field] || '';
          // Escape commas and quotes
          return `"${String(value).replace(/"/g, '""')}"`;
        })
      ];
      csv += row.join(',') + '\n';
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${form?.name || 'form'}_submissions_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/forms')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{form?.name}</h1>
            <p className="text-gray-600 mt-1">
              {submissions.length} {submissions.length === 1 ? 'submission' : 'submissions'}
            </p>
          </div>
        </div>
        {submissions.length > 0 && (
          <button
            onClick={exportToCSV}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export to CSV
          </button>
        )}
      </div>

      {submissions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No submissions yet</h3>
            <p className="text-gray-600">
              When users submit this form, their responses will appear here.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map((submission) => {
            // submission.data is already an object (JSONB from backend), no need to parse
            const data = submission.data;
            return (
              <div key={submission.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Submission #{submission.id}
                    </h3>
                    <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {format(new Date(submission.created_at), 'MMM dd, yyyy HH:mm')}
                      </div>
                      {submission.ip_address && (
                        <div className="flex items-center">
                          <Globe className="w-3 h-3 mr-1" />
                          {submission.ip_address}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(data).map(([key, value]) => (
                    <div key={key} className="border-l-2 border-blue-500 pl-3">
                      <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                        {key.replace(/_/g, ' ')}
                      </div>
                      <div className="text-sm text-gray-900">
                        {Array.isArray(value) ? value.join(', ') : String(value)}
                      </div>
                    </div>
                  ))}
                </div>

                {submission.referrer && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="text-xs text-gray-500">
                      <span className="font-medium">Referrer:</span> {submission.referrer}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FormSubmissions;
